// React imports
import { useEffect, useState, useRef, useCallback } from 'react';

// Third-party library imports
import { useQuery, useMutation } from '@tanstack/react-query';
import { theme } from 'antd';
import { Tooltip } from 'react-tooltip';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import { VscWand } from 'react-icons/vsc';
import { BsSkipEnd } from 'react-icons/bs';

// Contexts
import BaseText from '@components/typography/BaseText';
import { useProject } from '@contexts/Project.context';
import { useToast } from '@contexts/Toast.context';
import { useConfirm } from '@contexts/Confirm.context';

// Hooks
import useNotify from '@hooks/useNotify';
import useKeyboardShortcut from '@hooks/useKeyboardShortcut.hook';
import { useAnnotationActiveNode } from '@routes/userRoutes/projects/singleProject/components/sidebars/annotation/useAnnotationActiveNode';
import { useAnnotationSessionStore } from '@stores/useAnnotationSessionStore';

// Utils
import {
	setSquareBoundary,
	addAnnotationId,
	setAnnotationNode,
	setAnnotationTheme,
} from '@utils/map/annotation/annotation.interaction';
import {
	initAnnotationSquare,
	updateAnnotationSquare,
	removeAnnotationSquare,
} from '@utils/map/square.draw';
import {
	startObjectAnnotation,
	stopDrawingObjects,
	getObjectsInSquare,
	removeAllObjectsInSquare,
	setDrawInteraction as setObjectDrawInteraction,
	setObjectDrawingActive,
	setObjectMode,
	setObjectRotateEnabled,
	subscribeObjectMode,
} from '@utils/map/object.draw';
import {
	startPolygonAnnotation,
	getPolygonsInSquare,
	stopDrawingPolygons,
	removeAllPolygonsInSquare,
	setDrawInteraction as setPolygonDrawInteraction,
} from '@utils/map/polygon.draw';
import {
	addErasorInteraction,
	removeErasorInteraction,
	startErasor,
	stopErasor,
} from '@utils/map/erasor.interaction';
import { setLayersVisibilityByCustomIds } from '@utils/map/helpers';
import { filterGridByPolygon } from '@utils/map/freeHand.draw';
import { ANNOTATION_TARGET, MODEL_TYPE } from '@utils/constants';

// Components
import {
	AnnotationToolBar,
	KeyboardShortcut,
} from '@routes/userRoutes/projects/singleProject/components/SharedStyles';
import UtilityButton from './utilityButtons/UtilityButton';
import AnnotationDrawTools from './utilityButtons/AnnotationDrawTools';
import AnnotationCrosshair from './AnnotationCrosshair';
import MapLoadingIndicator from '../MapLoadingIndicator';
import AnnotationToolbar from './annotationToolbar/AnnotationToolbar';
import AnnotationTileGrid from './AnnotationTileGrid';
import { useObjectAnnotationSession } from './useObjectAnnotationSession';
import { tileContaining } from './annotationSession';
import { useObjectSessionKeys } from './useObjectSessionKeys';
import { useObjectSwitchGuard } from './useObjectSwitchGuard';
import { useTrainNewObject } from './trainObject/useTrainNewObject';
import TrainNewObjectModals from './trainObject/TrainNewObjectModals';
import { useRemoveSelectedBox } from './useRemoveSelectedBox';

// API
import { postTileData, initTraining, getAnnotationInfoForModel, getGrid } from '@utils/api';

const getTileAnnotations = ({
	modelType,
	squaresInAreaOfInterest,
	activeSquareIndex,
	taskId,
	addToast,
}) => {
	// Ensure activeSquareIndex is within bounds
	if (activeSquareIndex < 0 || activeSquareIndex >= squaresInAreaOfInterest.length)
		throw new Error('Invalid activeSquareIndex');

	const activeSquare = squaresInAreaOfInterest[activeSquareIndex];
	let data;

	switch (modelType) {
		case MODEL_TYPE.OBJECT_DETECTION:
			data = getObjectsInSquare(activeSquare, taskId, addToast);
			break;
		case MODEL_TYPE.SEGMENTATION:
			data = getPolygonsInSquare(activeSquare);
			break;
		default:
			throw new Error('Invalid modelType');
	}

	return data;
};

// A stable empty object, so a selector returning it does not re-render on every store change.
const EMPTY_SAVED = Object.freeze({});

// The map is in EPSG:3857, which is what the annotation calls take and return.
const ORTHOPHOTO_SERIALIZE = {
	projection: '3857',
	toRing: ring => ring,
	toCoords: coords => coords,
};

const removeAnnotationsInSquare = ({ modelType, currentTileName, annotations }) => {
	switch (modelType) {
		case MODEL_TYPE.OBJECT_DETECTION:
			removeAllObjectsInSquare(currentTileName);
			break;
		case MODEL_TYPE.SEGMENTATION:
			removeAllPolygonsInSquare(annotations);
			break;
		default:
			throw new Error('Invalid modelType');
	}
};

export default function AnnotateOrthophoto({ layersToShow, hideAnnotationSidebar }) {
	const {
		detections,
		mapObject,
		polygonLayer,
		project,
		pickedTask,
		modelType,
		annotationMode,
		annotationDrawTool,
		toolBarVisible,
		colorOptions,
		confirmModalContent,
		annotationSidebarData,
		projectMode,
		isRotateEnabled,
		dispatch,
	} = useProject();

	const { gridFeatures: squaresInAreaOfInterest, target } = annotationMode;

	// Object annotation (Collection view) has no model behind it: the object's
	// list is loaded from the backend and a tile save sends it back.
	const isObjectTarget = target === ANNOTATION_TARGET.OBJECT;
	const taskId = isObjectTarget ? null : pickedTask.model_uuid;

	const { checkForNotifications } = useNotify();
	const { addToast } = useToast();
	const { confirm } = useConfirm();

	// The object new boxes are drawn for. The draw interactions live outside
	// React, so the pick is handed to them here and cleared when the session ends.
	// A model session hands nothing over: its boxes keep the class palette and
	// nothing in it is muted or labelled.
	// Only an object session shows Train New Object, which waits on the counts refetch.
	const { node: activeNode, countsFetching } = useAnnotationActiveNode({
		trackCountsFetching: isObjectTarget,
	});
	useEffect(() => {
		setAnnotationNode(isObjectTarget ? activeNode : null);
		return () => setAnnotationNode(null);
	}, [activeNode, isObjectTarget]);

	// The label text on the boxes takes its font and colours from the theme.
	const { token } = theme.useToken();
	useEffect(() => {
		if (!isObjectTarget) return;
		setAnnotationTheme({
			fontFamily: token.fontFamily,
			fontSizeSM: token.fontSizeSM,
			colorText: token.colorText,
			colorBgContainer: token.colorBgContainer,
		});
	}, [
		isObjectTarget,
		token.fontFamily,
		token.fontSizeSM,
		token.colorText,
		token.colorBgContainer,
	]);

	const [loading, setLoading] = useState(false);

	// Draw or Select: a click on a box on the map flips it, so the toolbar follows.
	const [objectMode, setObjectModeState] = useState('draw');
	useEffect(() => {
		if (!isObjectTarget) return undefined;
		return subscribeObjectMode(setObjectModeState);
	}, [isObjectTarget]);

	// The Rotate toggle decides how a selected box is reshaped.
	useEffect(() => {
		if (isObjectTarget) setObjectRotateEnabled(isRotateEnabled);
	}, [isObjectTarget, isRotateEnabled]);

	const selectedBoxId = useAnnotationSessionStore(s => s.selectedBoxId);
	const removeSelected = useRemoveSelectedBox();

	// Model view steps through the tiles from the first; an object session shows
	// them all and starts with none open.
	const [activeSquareIndex, setActiveSquareIndex] = useState(isObjectTarget ? null : 0);

	// Session of an object: its list and tile saves, and the switch to another
	// object the panel picks (its grid is cut to the same boundary).
	const session = useObjectAnnotationSession({
		enabled: isObjectTarget,
		activeNode,
		serialize: ORTHOPHOTO_SERIALIZE,
		boundary: annotationMode.boundary,
		tileAoi: tileName =>
			squaresInAreaOfInterest.find(tile => tile.properties.tile_name === tileName)?.geometry
				.coordinates[0],
		// Model boxes carry their model's grid tile; file them under this session's tile.
		tileOfBox: (box, preferred) => tileContaining(squaresInAreaOfInterest, box, preferred),
		tileName:
			activeSquareIndex === null
				? undefined
				: squaresInAreaOfInterest[activeSquareIndex]?.properties.tile_name,
		// Throws when the grid cannot be fetched (the switch then stays put), so a
		// network failure is not mistaken for, and cached as, "no tiles in the area".
		loadTilesFor: async node => {
			const cached = useAnnotationSessionStore.getState().tilesByNode[node.uuid];
			if (cached?.length) return cached;
			const { grid_geojson } = await getGrid({
				project_uuid: project.uuid,
				node_uuid: node.uuid,
			});
			const grid = grid_geojson?.features ?? [];
			const tiles = annotationMode.boundary
				? filterGridByPolygon(grid, annotationMode.boundary)
				: grid;
			if (tiles.length) useAnnotationSessionStore.getState().setTiles(node.uuid, tiles);
			return tiles;
		},
		onSwitched: ({ tiles }) => {
			dispatch({
				type: 'setAnnotationMode',
				payload: { ...annotationMode, gridFeatures: tiles },
			});
			closeTile();
		},
	});

	// Tiles saved for the active object in this session: marked on the map, counted in the toolbar.
	const savedTiles = useAnnotationSessionStore(
		s => s.savedByNode[activeNode?.uuid] ?? EMPTY_SAVED
	);

	/** Open a tile from the map: zoom to it, outline it, and let boxes be drawn in it. */
	const openTile = index => {
		const tile = squaresInAreaOfInterest[index];
		if (!tile) return;
		setActiveSquareIndex(index);
		setSquareBoundary(tile);
		initAnnotationSquare(tile, mapObject);
		setObjectDrawingActive(true);
	};

	/** Leave the open tile: no outline, no drawing, the whole grid in view again. */
	const closeTile = () => {
		setActiveSquareIndex(null);
		setSquareBoundary(null);
		removeAnnotationSquare(mapObject);
		setObjectDrawingActive(false);
		if (isObjectTarget) setObjectMode('draw');
		else if (!drawing) startDraw();
	};

	// Save closes the tile, as in the design: a save covers the whole tile.
	const saveAndCloseTile = async () => {
		if (await session.saveTile()) closeTile();
	};

	// Cancel leaves the open tile at any time; what was done in it since the last
	// save is thrown away, after asking when there is something to lose.
	const cancelTile = async () => {
		if (session.hasUnsavedChanges()) {
			const confirmed = await confirm({
				title: 'Discard changes',
				message: 'The changes in this tile are not saved. Discard them and leave the tile?',
			});
			if (!confirmed) return;
		}
		session.discardTile();
		closeTile();
	};

	// Train New Object: its modals save the open tile the way the toolbar does.
	const train = useTrainNewObject({
		activeNode: isObjectTarget ? activeNode : null,
		projectMode,
		countsFetching,
		onSave: saveAndCloseTile,
		hasUnsavedBoxes: session.hasUnsavedBoxes,
	});

	const [drawing, setDrawing] = useState(true);

	const isTraining = useRef(false); // To prevent re-rendering of the annotation type

	const { mutate: startTraining } = useMutation({
		mutationFn: () =>
			initTraining({
				projectUuid: project.uuid,
				modelId: taskId,
			}),
		onError: err => {
			console.error('Error starting training from orthophoto annotate', err);
			addToast({
				id: 'training-error',
				className: 'bg-danger',
				title: `Training model '${pickedTask.description}' failed:`,
				message:
					err.response?.data?.detail || err.message || 'Unknown error starting training.',
				autohide: false,
			});
		},
		onSuccess: res => {
			if (res?.warning) {
				addToast({
					id: 'training-warning',
					className: 'bg-warning',
					title: `Training model '${pickedTask.description}' started with warnings:`,
					message: res.warning,
					autohide: true,
				});
			} else if (res?.error) {
				addToast({
					id: 'training-error',
					className: 'bg-danger',
					title: `Training model '${pickedTask.description}' failed:`,
					message: res.error,
					autohide: false,
				});
			} else {
				addToast({
					id: 'training-success',
					className: 'bg-success',
					title: `Training model '${pickedTask.description}' started successfully`,
					message: 'The process will take some time to complete.',
					autohide: true,
				});
			}
		},

		onSettled: () => {
			stopAnnotation();
			setTimeout(() => {
				checkForNotifications();
			}, 5000);
		},
	});

	const saveAndExit = async () => {
		const confirmContinue = await saveTileData();
		if (confirmContinue) stopAnnotation();
	};

	// End annotation session and start training.
	// Requires explicit user confirmation to prevent accidental training triggers
	// (e.g. pressing Enter/Space on the last tile).
	//
	// This is the single confirmation point for the whole training flow: both the
	// "Start AI training" button and keyboard completion land here, so the dialog
	// must not be duplicated in the button's own handler.
	//
	// The save is unconditional. An untouched tile still has to be persisted as
	// training data — a deliberately empty one is a valid negative example — and
	// saveTileData() owns the "no annotations" confirmation. Confirming before
	// saving is what keeps a dismissed dialog from causing a redundant POST.
	const endAnnotation = async () => {
		// Object sessions save per tile and leave through Exit; this is the model flow's end.
		if (isObjectTarget) return;

		const userConfirmed = await confirm({
			title: 'Start AI training',
			message: (
				<p>
					Are you sure you want to end the annotation session? Your data will be saved and
					AI training will start.
				</p>
			),
		});

		if (!userConfirmed) return;

		const confirmContinue = await saveTileData();
		if (!confirmContinue) return;

		startTraining();
	};

	// Stop annotation session
	const stopAnnotation = () => {
		console.log('stopping annotation');

		if (mapObject) {
			const layers = mapObject.getLayers();
			layers?.forEach(layer => {
				const name = layer.get('name');
				if (layersToShow.includes(name)) {
					layer.setVisible(true);
				}
			});
		}

		setActiveSquareIndex(null);
		removeAnnotationSquare(mapObject);
		removeErasorInteraction(mapObject);

		mapObject.on('pointermove', () => {
			mapObject.getTargetElement().style.cursor = 'auto';
		});

		let confirmStop = false;

		if (modelType === MODEL_TYPE.OBJECT_DETECTION) confirmStop = stopDrawingObjects(mapObject);
		else if (modelType === MODEL_TYPE.SEGMENTATION)
			confirmStop = stopDrawingPolygons(mapObject);

		if (confirmStop) {
			dispatch({
				type: 'setDialogue',
				payload: null,
			});
			dispatch({
				type: 'setToolBarVisible',
				payload: true,
			});
			dispatch({
				type: 'setAnnotationMode',
				payload: null,
			});
			dispatch({
				type: 'setAnnotationDrawTool',
				payload: null,
			});

			setLayersVisibilityByCustomIds(mapObject, layersToShow, true);
			hideAnnotationSidebar();
		}
	};

	// Start drawing
	const startDraw = () => {
		if (drawing) return;

		setDrawing(true);
		stopErasor(mapObject);
	};

	// Start erasing
	const startErase = () => {
		if (!drawing) return;

		setDrawing(false);
		startErasor(mapObject);
	};

	// Check if there are annotations in the current square before skipping
	const checkTileBeforeSkip = async () => {
		setLoading(true);

		const { annotations } = getTileAnnotations({
			modelType,
			squaresInAreaOfInterest,
			activeSquareIndex,
			taskId,
			addToast,
		});

		if (!annotations) {
			// Annotations should exist
			setLoading(false);
			return false;
		}

		let confirmContinue = true;
		// We need to check if there are any annotations that are not machine annotations
		const addedAnnotations = annotations.filter(annotation => !annotation.machine_annotation);
		if (addedAnnotations?.length > 0) {
			const userConfirmed = await confirm({
				title: 'Discard Annotations',
				message:
					'You have made new annotations in this square. Skipping will discard them. Still good to go?',
			});

			if (userConfirmed) {
				// Remove the annotations
				const currentTileName =
					squaresInAreaOfInterest[activeSquareIndex]?.properties.tile_name;
				removeAnnotationsInSquare({
					modelType,
					currentTileName,
					annotations: addedAnnotations,
				});
			} else confirmContinue = false;
		}

		setLoading(false);
		return confirmContinue;
	};

	// Save the annotations in the current square
	const saveTileData = async () => {
		if (isObjectTarget) return session.saveTile();

		setLoading(true);

		const data = getTileAnnotations({
			modelType,
			squaresInAreaOfInterest,
			activeSquareIndex,
			taskId,
			addToast,
		});

		if (!data) {
			setLoading(false);
			return false;
		}

		if (!data?.annotations || data?.annotations?.length === 0) {
			const userConfirmed = await confirm({
				title: 'No Annotations',
				message:
					"No new annotations were made in this square. It could affect the model if something's missed. Still good to go?",
			});

			if (!userConfirmed) {
				setLoading(false);
				return false;
			}

			// Backend does not want an empty tile to be posted if there are overlapping annotations
			if (data.hasOverlappedAnnotations) {
				console.log('Skipping saving empty tile with overlapping annotations');
				setLoading(false);
				return true;
			}
		}

		const currentTileName = squaresInAreaOfInterest[activeSquareIndex]?.properties.tile_name;

		const res = await postTileData(project?.uuid, taskId, currentTileName, data.annotations)
			.then(() => {
				return true;
			})
			.catch(error => {
				console.error('Error saving orthophoto annotation data', error);
				return false;
			})
			.finally(() => {
				setLoading(false);
			});

		return res;
	};

	// change the square to annotate
	const onChangeSquare = index => {
		setActiveSquareIndex(index);
		setSquareBoundary(squaresInAreaOfInterest[index]);
		updateAnnotationSquare(squaresInAreaOfInterest[index], mapObject);
	};

	// Move to the next square
	const nextSquare = async ({ skip = false }) => {
		if (loading) return;

		if (activeSquareIndex < squaresInAreaOfInterest.length - 1) {
			let confirmContinue = true;
			if (skip) {
				confirmContinue = await checkTileBeforeSkip();
				console.log('skipping tile');
			} else {
				confirmContinue = await saveTileData();
				console.log('saving tile');
			}

			if (confirmContinue) onChangeSquare(activeSquareIndex + 1);
		} else if (activeSquareIndex === squaresInAreaOfInterest.length - 1) endAnnotation();
	};

	// Handle Exit button with confirmation modal
	const handleExit = async () => {
		if (isObjectTarget) {
			const userConfirmed = await confirm({
				title: 'Exit annotation mode',
				message:
					'Unsaved boxes in the open tile are discarded; saved ones stay for this session. Exit anyway?',
			});
			if (userConfirmed) stopAnnotation();
			return;
		}

		const result = await confirm({
			title: 'Exit annotation mode',
			message: (
				<p>
					Previous tiles are saved automatically. <br />
					Do you want to save your current tile before leaving?
				</p>
			),
			confirmLabel: 'Save & Exit',
			cancelLabel: 'Exit',
		});

		if (result === true) {
			// Save & Exit (Confirm button clicked)
			await saveAndExit();
		} else if (result === false) {
			// Exit Without Saving (Cancel button clicked)
			stopAnnotation();
		}
		// If result === null, user dismissed the modal (X button) - do nothing, stay in annotation mode
	};

	// Keyboard shortcuts
	useKeyboardShortcut(
		'Enter',
		!isObjectTarget && !!annotationMode && confirmModalContent === null,
		() => nextSquare({ skip: false })
	);
	useKeyboardShortcut(
		' ',
		!isObjectTarget && !!annotationMode && confirmModalContent === null,
		() => nextSquare({ skip: true })
	);
	useKeyboardShortcut(
		'Escape',
		!isObjectTarget && !toolBarVisible && confirmModalContent === null,
		() => handleExit()
	);
	// Picking another object with unsaved changes in the open tile asks to save first.
	useObjectSwitchGuard({
		enabled: isObjectTarget,
		session,
		isOpen: activeSquareIndex !== null,
		objectName: activeNode?.name,
	});
	// The shortcuts listen on `window`, so an open dialog must switch them off.
	const dialogOpen = confirmModalContent !== null || train.isOpen;
	useObjectSessionKeys({
		enabled: isObjectTarget,
		escapeEnabled: !toolBarVisible && !dialogOpen,
		canRemove: Boolean(selectedBoxId) && !dialogOpen,
		onExit: handleExit,
		onRemove: removeSelected,
	});

	useEffect(() => {
		// change the relevant draw interaction to use the correct draw tool

		if (modelType === MODEL_TYPE.SEGMENTATION) {
			setPolygonDrawInteraction({
				map: mapObject,
				freehand: annotationDrawTool === 'Freehand',
				colorOptions,
			});
		} else if (modelType === MODEL_TYPE.OBJECT_DETECTION) {
			setObjectDrawInteraction({
				mapReference: mapObject,
				drawTool: annotationDrawTool,
			});
		}
	}, [annotationDrawTool]);

	useEffect(() => {
		const { activeClassId } = annotationSidebarData || {
			activeClassId: 0,
		};
		addAnnotationId(activeClassId);

		// if erasor is active when changing the annotation type
		// we need to start drawing again
		if (!drawing) startDraw();
	}, [annotationSidebarData]);

	// Starts the right annotation type
	useEffect(() => {
		if ((activeSquareIndex === null && !isObjectTarget) || isTraining.current) return;

		isTraining.current = true;
		console.log('Starting annotation mode');

		if (modelType === MODEL_TYPE.OBJECT_DETECTION) {
			console.log('Starting object annotation');
			startObjectAnnotation({
				colorOptionsInput: colorOptions,
				existingObjects: isObjectTarget ? [] : detections,
				squareList: squaresInAreaOfInterest,
				mapReference: mapObject,
				scoped: isObjectTarget,
			});
			if (isObjectTarget) {
				// The active object's list: its model boxes and annotated tiles.
				session.track();
				session.loadNode(activeNode);
				// Nothing can be drawn until a tile is opened from the map.
				setObjectDrawingActive(false);
			}
		} else if (modelType === MODEL_TYPE.SEGMENTATION) {
			console.log('Starting polygon annotation');
			startPolygonAnnotation({
				colorOptions,
				existingPolygonLayer: isObjectTarget ? null : polygonLayer,
				squareList: squaresInAreaOfInterest,
				mapReference: mapObject,
			});
		}

		dispatch({
			type: 'setDialogue',
			payload: {
				header: 'Annotating',
				body: isObjectTarget ? (
					<>
						<p>
							Click a tile to open it, draw a box around every{' '}
							{activeNode?.name ?? 'object'} in it, then click "Save tile".
						</p>
						<p>
							<BaseText size="xs" type="secondary">
								A save covers the whole tile and closes it. Pick another object in
								the sidebar to annotate it in the same area.
							</BaseText>
						</p>
					</>
				) : (
					<>
						<p>Annotate all relevant items in the square before clicking "Continue".</p>
						<p className="small text-muted">
							Also use "Continue" on tiles without target items to help the model
							learn to recognize empty areas. Only use "Skip" if you don't want to
							train the model on this tile.
						</p>
					</>
				),
				fullWidth: false,
				autoClose: true,
				dismissible: true,
			},
		});

		if (!isObjectTarget) {
			initAnnotationSquare(squaresInAreaOfInterest[activeSquareIndex], mapObject); // draws the first square
			setSquareBoundary(squaresInAreaOfInterest[activeSquareIndex]);
		}

		if (!isObjectTarget) addErasorInteraction(modelType, mapObject);
	}, []);

	return (
		<>
			<AnnotationCrosshair
				map={mapObject}
				active={
					isObjectTarget ? objectMode === 'draw' && activeSquareIndex !== null : drawing
				}
			/>

			{/* The object's annotation list is loading: at session start, or after a switch. */}
			{isObjectTarget && (session.loading || session.switching) && (
				<MapLoadingIndicator
					message={`Loading ${activeNode?.name ?? 'object'} annotations`}
					hint="this can take a moment"
				/>
			)}

			{isObjectTarget && (
				<AnnotationTileGrid
					map={mapObject}
					tiles={squaresInAreaOfInterest}
					activeIndex={activeSquareIndex}
					savedTiles={savedTiles}
					onPick={openTile}
				/>
			)}

			{isObjectTarget && <TrainNewObjectModals {...train.modals} />}

			{isObjectTarget ? (
				<AnnotationToolbar
					onExit={handleExit}
					mode={objectMode}
					onModeChange={setObjectMode}
					keysDisabled={dialogOpen}
					onRemove={removeSelected}
					canRemove={Boolean(selectedBoxId)}
					disabled={
						loading ||
						session.loading ||
						session.saving ||
						session.switching ||
						activeSquareIndex === null
					}
					loading={loading || session.loading || session.saving || session.switching}
					onSave={saveAndCloseTile}
					canSave={session.canSave}
					saveEmphasis={session.hasUnsavedBoxes}
					onTrain={train.button.onTrain}
					trainDisabled={train.button.disabled}
					trainTooltip={train.button.tooltip}
					savesEmpty={session.savesEmpty}
					objectName={activeNode?.name}
					onCancel={cancelTile}
					progress={{
						index: activeSquareIndex,
						total: squaresInAreaOfInterest.length,
						unit: 'tile',
						saved: squaresInAreaOfInterest.filter(
							tile => savedTiles[tile.properties.tile_name] !== undefined
						).length,
					}}
				/>
			) : (
				<AnnotationToolBar aria-label="Annotation tools">
					<Tooltip
						id="annotate-tip"
						variant="light"
						render={({ content, activeAnchor }) => (
							<span>
								{content}{' '}
								<KeyboardShortcut>
									{activeAnchor?.getAttribute('data-tooltip-keyboardshortcut')}
								</KeyboardShortcut>
							</span>
						)}
					/>

					<div>
						<UtilityButton
							label="Exit"
							tooltip={{
								id: 'annotate-tip',
								content: 'Exit annotation mode',
								place: 'top',
							}}
							onClick={handleExit}
							variant="dark"
							keyboardShortcutLabel="ESC"
						/>
					</div>

					<AnnotationDrawTools
						startDraw={startDraw}
						startErase={startErase}
						drawing={drawing}
					/>

					<div className="d-flex gap-3 align-items-center">
						<div>
							Annotating {activeSquareIndex + 1} / {squaresInAreaOfInterest.length}
						</div>
						<ButtonGroup>
							<StartAITrainingButton
								taskId={taskId}
								squaresInAreaOfInterest={squaresInAreaOfInterest}
								activeSquareIndex={activeSquareIndex}
								endAnnotation={endAnnotation}
								loading={loading}
							/>

							{activeSquareIndex < squaresInAreaOfInterest.length - 1 && (
								<>
									<UtilityButton
										label="Skip"
										tooltip={{
											id: 'annotate-tip',
											content:
												'Ignore the current tile and move to the next tile',
											place: 'top',
										}}
										disabled={loading}
										loading={loading}
										onClick={() => nextSquare({ skip: true })}
										variant="dark"
										keyboardShortcutLabel="␣"
										icon={() => <BsSkipEnd />}
									/>
									<UtilityButton
										label="Continue"
										tooltip={{
											id: 'annotate-tip',
											content:
												'Save the annotations in the current tile and move to the next tile',
											place: 'top',
										}}
										disabled={loading}
										loading={loading}
										onClick={nextSquare}
										variant="success"
										keyboardShortcutLabel="↵"
									/>
								</>
							)}
						</ButtonGroup>
					</div>
				</AnnotationToolBar>
			)}
		</>
	);
}

const StartAITrainingButton = ({
	taskId,
	squaresInAreaOfInterest,
	activeSquareIndex,
	endAnnotation,
	loading,
}) => {
	const { modelType, dispatch } = useProject();
	const { addToast } = useToast();

	const { data: annotationInfo } = useQuery({
		queryKey: ['annotationInfo', taskId, activeSquareIndex],
		queryFn: () => getAnnotationInfoForModel(taskId),
		enabled: !!taskId,
		retry: 0,
	});
	const {
		can_be_trained: canBeTrained,
		total_number_of_annotations: existingAnnotations,
		total_number_of_tiles: annotatedTiles,
	} = annotationInfo || { can_be_trained: true };

	const saveAndTrain = useCallback(async () => {
		const checkTrainingData = () => {
			try {
				const { annotations } = getTileAnnotations({
					modelType,
					squaresInAreaOfInterest,
					activeSquareIndex,
					taskId,
					addToast,
				});

				const totalAnnotations = (annotations?.length ?? 0) + existingAnnotations;
				const totalTiles = annotatedTiles + 1;

				// No data to check so let the backend decide
				if (!totalAnnotations || !totalTiles) return true;

				if (totalAnnotations < 10 && totalTiles < 6) {
					addToast({
						id: `training_warning`,
						className: 'bg-warning',
						autohide: false,
						title: `Not enough data to start training`,
						message: (
							<>
								You need to have at least <strong>10 annotations</strong> and{' '}
								<strong>6 annotated</strong> tiles to start training.
							</>
						),
					});
					return false;
				}

				return true;
			} catch (error) {
				console.warn(
					'Could not check if data can be trained. Letting the backend decide.',
					error
				);
				return true;
			}
		};

		if (!canBeTrained && !checkTrainingData()) return;

		// No confirmation here: endAnnotation() owns the single "Start AI training"
		// dialog for both this button and keyboard completion. Confirming here too
		// showed the same dialog twice.
		await endAnnotation();
	}, [annotationInfo, dispatch, endAnnotation]);

	return (
		<UtilityButton
			label="Start AI training"
			tooltip={{
				id: 'annotate-tip',
				content: 'End annotation session and start AI training',
				place: 'top',
			}}
			onClick={saveAndTrain}
			disabled={loading}
			variant="dark"
			icon={() => <VscWand />}
		/>
	);
};
